import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type PageChromeSetters = {
  setHero: (hero: ReactNode) => void;
  setHeroAux: (heroAux: ReactNode) => void;
  /**
   * Overrides the desktop top-bar page label with a page-specific breadcrumb
   * (e.g. "Products › {name}" on the edit page). Pass null to fall back to the
   * active nav item's icon + label.
   */
  setBreadcrumb: (breadcrumb: ReactNode) => void;
  /**
   * Hides the phone's primary bottom nav while a page-owned bottom bar is
   * mounted (the product edit "تم الحفظ / معاينة" bar). Returns the release.
   * Counted, so two holders never un-hide each other.
   */
  holdBottomNav: () => () => void;
};

type PageChromeValues = {
  hero: ReactNode;
  heroAux: ReactNode;
  breadcrumb: ReactNode;
  bottomNavHidden: boolean;
};

// Split into two contexts so that pages which only *set* the chrome do not
// re-render when the chrome *value* changes. If a single context carried both,
// calling setHero() would change the context value and re-render every
// consumer — including the page that just called setHero from an effect, whose
// effect deps (e.g. a fresh `t` from useTranslations) would then refire the
// effect → setHero → render → loop ("Maximum update depth exceeded").
const PageChromeSettersContext = createContext<PageChromeSetters | null>(null);
const PageChromeValuesContext = createContext<PageChromeValues | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [hero, setHero] = useState<ReactNode>(null);
  const [heroAux, setHeroAux] = useState<ReactNode>(null);
  const [breadcrumb, setBreadcrumb] = useState<ReactNode>(null);
  const [bottomNavHolds, setBottomNavHolds] = useState(0);
  const holdBottomNav = useCallback(() => {
    setBottomNavHolds((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setBottomNavHolds((n) => Math.max(0, n - 1));
    };
  }, []);

  // Stable for the provider's lifetime — useState setters never change identity,
  // so this memo (no deps) is computed once and consumers of the setters context
  // never re-render due to chrome updates.
  const setters = useMemo<PageChromeSetters>(
    () => ({ setHero, setHeroAux, setBreadcrumb, holdBottomNav }),
    [holdBottomNav],
  );
  const values = useMemo<PageChromeValues>(
    () => ({ hero, heroAux, breadcrumb, bottomNavHidden: bottomNavHolds > 0 }),
    [hero, heroAux, breadcrumb, bottomNavHolds],
  );

  return (
    <PageChromeSettersContext.Provider value={setters}>
      <PageChromeValuesContext.Provider value={values}>
        {children}
      </PageChromeValuesContext.Provider>
    </PageChromeSettersContext.Provider>
  );
}

/** Setters only — use from pages to inject the desktop hero/heroAux chrome. */
export function usePageChrome(): PageChromeSetters {
  const ctx = useContext(PageChromeSettersContext);
  if (!ctx) {
    throw new Error("usePageChrome must be used within PageChromeProvider");
  }
  return ctx;
}

/** Current chrome values — use from the shell region that renders the chrome. */
export function usePageChromeValues(): PageChromeValues {
  const ctx = useContext(PageChromeValuesContext);
  if (!ctx) {
    throw new Error("usePageChromeValues must be used within PageChromeProvider");
  }
  return ctx;
}

/**
 * While `active`, the phone's primary bottom nav is hidden. Safe outside the
 * provider (a no-op), so a component rendered in isolation still mounts.
 */
export function useSuppressPrimaryNav(active: boolean): void {
  const ctx = useContext(PageChromeSettersContext);
  const hold = ctx?.holdBottomNav;
  useEffect(() => {
    if (!active || !hold) return;
    return hold();
  }, [active, hold]);
}

/** Whether a page has hidden the bottom nav; false outside the provider. */
export function usePrimaryNavSuppressed(): boolean {
  return useContext(PageChromeValuesContext)?.bottomNavHidden ?? false;
}
